import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import AdminLayout from "../../layouts/AdminLayout";
import { getProjectProgress, updateProjectDiscipline, deleteProjectDiscipline } from "../../api/projectService";

// ── Sort disciplines by trailing number, then alphabetically ──────────────────────
const sortDisciplines = (disciplines) =>
  [...disciplines].sort((a, b) => {
    const numA = parseInt((a.discipline_id.match(/(\d+)$/) || [0, 0])[1], 10);
    const numB = parseInt((b.discipline_id.match(/(\d+)$/) || [0, 0])[1], 10);
    if (numA !== numB) return numA - numB;
    return a.discipline_id.localeCompare(b.discipline_id);
  });

// ── Row background matching ProjectList colors ────────────────────────────────
const getRowBackground = (status) => {
  if (status === "COMPLETED") return "bg-emerald-400/40 hover:bg-emerald-400/50";
  if (status === "ON_HOLD") return "bg-gray-500/30 hover:bg-gray-500/40";
  return "bg-white/10 hover:bg-white/20";
};

// ── Status badge for mobile cards ─────────────────────────────────────────────
const getStatusBadgeClass = (status) => {
  if (status === "COMPLETED") return "bg-emerald-400/30 text-emerald-300 border-emerald-400/40";
  if (status === "ON_HOLD") return "bg-gray-500/30 text-gray-300 border-gray-400/40";
  if (status === "IN_PROGRESS") return "bg-blue-400/30 text-blue-300 border-blue-400/40";
  return "bg-white/10 text-white/70 border-white/20";
};


export default function ProjectDetails({ role = "MANAGER", readOnly = false, setProjects }) {
  const { projectId } = useParams();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingDiscipline, setUpdatingDiscipline] = useState(null);
  const [editingDisciplineId, setEditingDisciplineId] = useState(null);
  const [stagesByDiscipline, setStagesByDiscipline] = useState({});

  const isAdmin = role === "ADMIN";
  const Layout = isAdmin ? AdminLayout : ManagerLayout;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disciplineToDelete, setDisciplineToDelete] = useState("");
  const [confirmInput, setConfirmInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  const openDeleteModal = (disciplineId) => {
    setDisciplineToDelete(disciplineId);
    setConfirmInput("");
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDisciplineToDelete("");
    setConfirmInput("");
  };

  const handleConfirmDelete = async () => {
    if (confirmInput.trim().toUpperCase() !== disciplineToDelete.trim().toUpperCase()) {
      alert("Discipline name mismatch! Please type the exact name to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await deleteProjectDiscipline(projectId, disciplineToDelete);
      alert(`Discipline ${disciplineToDelete} deleted successfully!`);
      closeDeleteModal();
      await fetchProject();
    } catch (err) {
      alert(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Failed to delete discipline."
      );
    } finally {
      setDeleting(false);
    }
  };

  /* ================= FETCH PROJECT ================= */
  const fetchProject = useCallback(async () => {
    try {
      const res = await getProjectProgress(projectId);

      const normalizedDisciplines = (res.data.project_progress || []).map(discipline => ({
        ...discipline,
        status: discipline.status ? discipline.status.toUpperCase() : "PLANNED",
      }));

      const sortedDisciplines = sortDisciplines(normalizedDisciplines);

      setProject({ ...res.data, project_progress: sortedDisciplines });

      if (setProjects) {
        setProjects(prev =>
          prev.map(p =>
            p.project_id === res.data.project_id
              ? { ...p, status: res.data.status }
              : p
          )
        );
      }

      // Build stages map from API response
      const stagesMap = {};
      sortedDisciplines.forEach((discipline) => {
        stagesMap[discipline.discipline_id] = (discipline.stages || []).map(s => ({
          stage_id: s.stage_id,
          name: s.stage_name,
          description: s.description,
        }));
      });
      setStagesByDiscipline(stagesMap);

    } catch (err) {
      console.error("Failed to load project progress", err);
    }
  }, [projectId, setProjects]);

  useEffect(() => {
    setLoading(true);
    fetchProject().finally(() => setLoading(false));
  }, [fetchProject]);

  /* ================= DISCIPLINE STATUS UPDATE ================= */
  const handleStatusChange = async (disciplineId, newStatus) => {
    if (!project) return;

    const prevDisciplines = [...project.project_progress];

    const updatedDisciplines = project.project_progress.map(s =>
      s.discipline_id === disciplineId
        ? {
          ...s,
          status: newStatus,
          completed_at: newStatus === "COMPLETED" ? new Date().toISOString() : null
        }
        : s
    );

    setProject({ ...project, project_progress: updatedDisciplines });
    setUpdatingDiscipline(disciplineId);

    try {
      await updateProjectDiscipline(projectId, disciplineId, { status: newStatus });
      await fetchProject();
    } catch (err) {
      console.error("Failed to update discipline status", err);
      setProject({ ...project, project_progress: prevDisciplines });
    } finally {
      setUpdatingDiscipline(null);
    }
  };

  const [updatingDisciplineId, setUpdatingDisciplineId] = useState(null);

  const handleDisciplineHoursChange = (index, value) => {
    if (!project) return;
    const updatedDisciplines = [...project.project_progress];
    updatedDisciplines[index].total_estimated_hours = value;
    setProject({ ...project, project_progress: updatedDisciplines });
  };

  const handleUpdateHours = async (discipline) => {
    setUpdatingDisciplineId(discipline.discipline_id);
    try {
      await updateProjectDiscipline(projectId, discipline.discipline_id, {
        estimated_hours: discipline.total_estimated_hours ? parseFloat(discipline.total_estimated_hours) : 0.0
      });
      alert(`Discipline ${discipline.discipline_id} updated successfully!`);
      setEditingDisciplineId(null);
      await fetchProject();
    } catch (err) {
      alert(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Failed to update estimated hours."
      );
    } finally {
      setUpdatingDisciplineId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 sm:p-10 text-center text-white">
          Loading project progress...
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="p-6 sm:p-10 text-center text-red-300">
          Failed to load project.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3 sm:p-6 max-w-[1400px] mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 sm:p-8 mb-4 sm:mb-6"
        >
          <h2 className="text-center text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">
            View Project Details
          </h2>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="text-white space-y-1">
              <p className="font-semibold text-sm sm:text-base">Project ID : {project.project_id}</p>
              <p className="font-semibold text-sm sm:text-base">Project Name : {project.project_name}</p>
            </div>

            {!readOnly && !isAdmin && (
              <Link to={`/manager/projects/${projectId}/create-discipline`} className="self-start sm:self-auto">
                <button className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold shadow-lg hover:scale-105 transition-all text-sm sm:text-base">
                  Add New Discipline
                </button>
              </Link>
            )}
          </div>
        </motion.div>

        {/* ── Desktop Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="hidden lg:block bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-white">
              <thead className="bg-white/20 border-b border-white/30">
                <tr>
                  <th className="p-4 text-left pl-6 w-32">Discipline ID</th>
                  <th className="p-4 text-left">Stages</th>
                  <th className="p-4 text-center w-52">Estimated Hours</th>
                  {!readOnly && !isAdmin && (
                    <th className="p-4 text-center w-40">Action</th>
                  )}
                  <th className="p-4 text-center w-40">Status</th>
                </tr>
              </thead>

              <tbody>
                {project.project_progress.length === 0 ? (
                  <tr>
                    <td colSpan={!readOnly && !isAdmin ? 5 : 4} className="p-6 text-white/60 text-center">
                      No disciplines created yet.
                    </td>
                  </tr>
                ) : (
                  project.project_progress.map((discipline, index) => {
                    const stages = stagesByDiscipline[discipline.discipline_id] || [];

                    return (
                      <tr
                        key={discipline.discipline_id}
                        className={`border-b border-white/10 transition ${getRowBackground(discipline.status)}`}
                      >
                        {/* Discipline ID */}
                        <td className="p-4 font-bold text-left pl-6">{discipline.discipline_id}</td>

                        {/* Stages */}
                        <td className="p-4">
                          {stages.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {stages.map((stg, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-400/20 border border-blue-400/40 text-blue-200 text-xs whitespace-nowrap"
                                >
                                  <span>📄</span>
                                  <span>{stg.name}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <span className="text-white/40 text-xs italic">No stages yet</span>
                              {!readOnly && !isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => openDeleteModal(discipline.discipline_id)}
                                  className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 text-red-200 text-xs font-semibold shadow-md transition-all hover:scale-105"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Estimated Hours */}
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {editingDisciplineId === discipline.discipline_id ? (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  value={discipline.total_estimated_hours ?? ""}
                                  onChange={(e) => handleDisciplineHoursChange(index, e.target.value)}
                                  className="bg-white/10 border border-white/30 rounded-lg px-2.5 py-1 text-white text-xs w-24 text-center focus:outline-none focus:border-cyan-400"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => handleUpdateHours(discipline)}
                                  disabled={updatingDisciplineId === discipline.discipline_id}
                                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold text-xs shadow-md transition disabled:opacity-60"
                                >
                                  {updatingDisciplineId === discipline.discipline_id ? "Saving..." : "Update"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setEditingDisciplineId(null); fetchProject(); }}
                                  className="text-white/50 hover:text-white text-xs px-1"
                                >
                                  ✕
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">{discipline.total_estimated_hours || 0} hrs</span>
                                {!readOnly && !isAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => setEditingDisciplineId(discipline.discipline_id)}
                                    className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-xs shadow-md transition ml-2"
                                  >
                                    Edit
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>

                        {/* Action */}
                        {!readOnly && !isAdmin && (
                          <td className="p-4 text-center">
                            <Link to={`/manager/projects/${project.project_id}/disciplines/${discipline.discipline_id}/stages`}>
                              <button className="px-4 py-1.5 rounded-lg bg-yellow-400 text-black font-semibold hover:scale-105 hover:bg-yellow-300 transition-all text-xs">
                                Add Stages
                              </button>
                            </Link>
                          </td>
                        )}

                        {/* Status */}
                        <td className="p-4 text-center">
                          {!isAdmin ? (
                            <select
                              value={discipline.status}
                              disabled={updatingDiscipline === discipline.discipline_id}
                              onChange={(e) => handleStatusChange(discipline.discipline_id, e.target.value)}
                              className="bg-white/20 border border-white/30 rounded-xl px-2 py-1 text-xs text-white"
                            >
                              <option value="PLANNED" className="bg-gray-800 text-white">Planned</option>
                              <option value="IN_PROGRESS" className="bg-gray-800 text-white">In Progress</option>
                              <option value="COMPLETED" className="bg-gray-800 text-white">Completed</option>
                              <option value="ON_HOLD" className="bg-gray-800 text-white">On Hold</option>
                            </select>
                          ) : (
                            <span className="px-2 py-1 rounded-lg text-xs bg-white/10 border border-white/20">
                              {discipline.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ── Mobile / Tablet Cards ── */}
        <div className="lg:hidden space-y-3">
          {project.project_progress.length === 0 ? (
            <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-white/60 text-center">
              No disciplines created yet.
            </div>
          ) : (
            project.project_progress.map((discipline, i) => {
              const stages = stagesByDiscipline[discipline.discipline_id] || [];

              return (
                <motion.div
                  key={discipline.discipline_id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={`border border-white/20 rounded-2xl overflow-hidden shadow-lg ${getRowBackground(discipline.status)}`}
                >
                  {/* Card Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/15 bg-white/10">
                    <span className="text-white font-bold text-sm">{discipline.discipline_id}</span>
                    <div className="flex items-center gap-2">
                      {isAdmin ? (
                        <span className={`px-2 py-0.5 rounded-lg text-xs border font-semibold ${getStatusBadgeClass(discipline.status)}`}>
                          {discipline.status}
                        </span>
                      ) : (
                        <select
                          value={discipline.status}
                          disabled={updatingDiscipline === discipline.discipline_id}
                          onChange={(e) => handleStatusChange(discipline.discipline_id, e.target.value)}
                          className="bg-white/20 border border-white/30 rounded-lg px-2 py-1 text-xs text-white"
                        >
                          <option value="PLANNED" className="bg-gray-800 text-white">Planned</option>
                          <option value="IN_PROGRESS" className="bg-gray-800 text-white">In Progress</option>
                          <option value="COMPLETED" className="bg-gray-800 text-white">Completed</option>
                          <option value="ON_HOLD" className="bg-gray-800 text-white">On Hold</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3">

                    {/* Stages Section */}
                    <div className="bg-white/10 rounded-xl p-3">
                      <p className="text-white/50 text-xs mb-2">Stages</p>
                      {stages.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {stages.map((stg, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-400/20 border border-blue-400/40 text-blue-200 text-xs whitespace-nowrap"
                            >
                              <span>📄</span>
                              <span>{stg.name}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-white/40 text-xs italic">No stages yet</span>
                          {!readOnly && !isAdmin && (
                            <button
                              type="button"
                              onClick={() => openDeleteModal(discipline.discipline_id)}
                              className="px-2.5 py-1 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 text-red-200 text-xs font-semibold shadow-md transition-all"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Estimated Hours Section */}
                    <div className="bg-white/10 rounded-xl p-3 flex items-center justify-between">
                      <span className="text-white/50 text-xs">Estimated Hours</span>
                      <div className="flex items-center gap-2">
                        {editingDisciplineId === discipline.discipline_id ? (
                          <>
                            <input
                              type="number"
                              min="0"
                              value={discipline.total_estimated_hours ?? ""}
                              onChange={(e) => handleDisciplineHoursChange(i, e.target.value)}
                              className="bg-white/10 border border-white/30 rounded-lg px-2 py-1 text-white text-xs w-20 text-center focus:outline-none focus:border-cyan-400"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleUpdateHours(discipline)}
                              disabled={updatingDisciplineId === discipline.discipline_id}
                              className="px-2.5 py-1 rounded-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold text-xs shadow-md transition disabled:opacity-60"
                            >
                              {updatingDisciplineId === discipline.discipline_id ? "Saving..." : "Update"}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setEditingDisciplineId(null); fetchProject(); }}
                              className="text-white/50 hover:text-white text-xs px-1"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="text-white text-xs font-semibold">{discipline.total_estimated_hours || 0} hrs</span>
                            {!readOnly && !isAdmin && (
                              <button
                                type="button"
                                onClick={() => setEditingDisciplineId(discipline.discipline_id)}
                                className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-xs shadow-md transition ml-2"
                              >
                                Edit
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    {!readOnly && !isAdmin && (
                      <Link
                        to={`/manager/projects/${project.project_id}/disciplines/${discipline.discipline_id}/stages`}
                        className="block"
                      >
                        <button className="w-full px-4 py-2 rounded-xl bg-yellow-400 text-black font-semibold hover:scale-[1.02] hover:bg-yellow-300 transition-all text-sm">
                          Add Stages
                        </button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Back link */}
        <div className="mt-4 sm:mt-6">
          <Link
            to={isAdmin ? "/admin/projects" : "/manager/projects"}
            className="text-blue-300 font-semibold hover:underline text-sm sm:text-base"
          >
            ← Back to Projects
          </Link>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-zinc-900 border border-red-500/30 rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition"
              >
                ✕
              </button>
              
              <div className="flex items-center gap-3 text-red-400 mb-4">
                <span className="text-2xl">⚠️</span>
                <h3 className="text-lg font-bold text-white">Delete Discipline</h3>
              </div>
              
              <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                Are you sure you want to delete the discipline <strong className="text-red-400 font-mono">{disciplineToDelete}</strong>? This will permanently remove all associated employee assignments. This action cannot be undone.
              </p>
              
              <div className="space-y-2 mb-6">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Type <span className="text-white font-mono font-bold bg-white/10 px-1.5 py-0.5 rounded">{disciplineToDelete}</span> to confirm:
                </label>
                <input
                  type="text"
                  placeholder="Enter discipline ID"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  className="w-full bg-black/40 border border-white/20 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-red-500 transition-all font-mono"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleting || confirmInput.trim().toUpperCase() !== disciplineToDelete.trim().toUpperCase()}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white font-bold text-xs shadow-lg hover:scale-105 transition disabled:opacity-40 disabled:pointer-events-none"
                >
                  {deleting ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}